import React from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, Info, ExternalLink } from 'lucide-react'

interface TwitterConnectionStatusProps {
  isConnected: boolean
  connectionError?: string | null
  showInstructions?: boolean
}

const TwitterConnectionStatus: React.FC<TwitterConnectionStatusProps> = ({
  isConnected,
  connectionError,
  showInstructions = false
}) => {
  // If connected and no errors, show success state
  if (isConnected && !connectionError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6"
      >
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <div>
            <h3 className="text-green-400 font-semibold">Twitter API Connected</h3>
            <p className="text-green-300 text-sm">Real-time data streaming from @LunaticsBTC mentions</p>
          </div>
        </div>
      </motion.div>
    )
  }

  // Show setup instructions if no API configured
  if (!isConnected && !connectionError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6"
      >
        <div className="flex items-center space-x-3">
          <Info className="w-5 h-5 text-yellow-400" />
          <div className="flex-1">
            <h3 className="text-yellow-400 font-semibold">Twitter API Setup Required</h3>
            <p className="text-yellow-300 text-sm">
              Configure your Twitter API credentials to see real @LunaticsBTC community data
            </p>
          </div>
          <a
            href="https://developer.twitter.com/en/docs/twitter-api/getting-started/getting-access-to-the-twitter-api"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-xs flex items-center space-x-1"
          >
            <span>Get API Access</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        
        {showInstructions && (
          <div className="mt-4 p-3 bg-dark-800/50 rounded-lg">
            <h4 className="text-white text-sm font-semibold mb-2">Setup Instructions:</h4>
            <ol className="text-dark-300 text-sm space-y-1 list-decimal list-inside">
              <li>Get Twitter API access from developer.twitter.com</li>
              <li>Add your Bearer Token to .env.local: <code className="bg-dark-700 px-1 rounded text-xs">VITE_TWITTER_BEARER_TOKEN=your_token</code></li>
              <li>Set <code className="bg-dark-700 px-1 rounded text-xs">VITE_ENABLE_REAL_DATA=true</code></li>
              <li>Restart the development server</li>
            </ol>
          </div>
        )}
      </motion.div>
    )
  }

  // Show error state
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6"
    >
      <div className="flex items-center space-x-3">
        <XCircle className="w-5 h-5 text-red-400" />
        <div>
          <h3 className="text-red-400 font-semibold">Twitter API Connection Issue</h3>
          <p className="text-red-300 text-sm">
            {connectionError || 'Unable to connect to Twitter API'}
          </p>
        </div>
      </div>
      
      {showInstructions && (
        <div className="mt-4 p-3 bg-dark-800/50 rounded-lg">
          <h4 className="text-white text-sm font-semibold mb-2">Troubleshooting:</h4>
          <ul className="text-dark-300 text-sm space-y-1 list-disc list-inside">
            <li>Check your Twitter API credentials in .env.local</li>
            <li>Ensure your API key has the correct permissions</li>
            <li>Verify rate limits haven't been exceeded</li>
            <li>Check your internet connection</li>
          </ul>
        </div>
      )}
    </motion.div>
  )
}

export default TwitterConnectionStatus 